import LabelBackground from '@/components/LabelBackground/LabelBackground';
import WordMarkLogo from '@/components/WordMarkLogo/WordMarkLogo';
import useIsNative from '@/hooks/native/useIsNative';
import useKeyValueStorage from '@/hooks/storage/useKeyValueStorage';
import useSeason, { Season, seasons } from '@/hooks/useSeason';
import { useReducedMotion } from 'framer-motion';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SplashScreen = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const reduceMotion = useReducedMotion();
	const season = useSeason();

	// Only show the splash screen if it hasn't been shown in the last hour
	const {
		value: splashScreenLastShownDate,
		setValue: setSplashScreenLastShownDate,
	} = useKeyValueStorage<string | null>('splashScreenLastShownDate', null);
	const isSplashScreenShownInLastHour = useMemo(() => {
		if (!splashScreenLastShownDate) return false;
		return (
			new Date().getTime() -
				new Date(splashScreenLastShownDate).getTime() <
			60 * 60 * 1000
		);
	}, [splashScreenLastShownDate]);

	// Move the seasons array as a whole so it ends with the current season
	const seasonsSortedCurrentOneLast = useMemo(() => {
		const index = seasons.indexOf(season);
		return [...seasons.slice(index + 1), ...seasons.slice(0, index + 1)];
	}, [season]);

	const [currentlyShownSeasons, setCurrentlyShownSeasons] = useState<
		Season[]
	>([]);
	const [shouldMountSplashScreen, setShouldMountSplashScreen] = useState(
		!reduceMotion &&
			!isSplashScreenShownInLastHour &&
			location.pathname === '/',
	);

	useEffect(() => {
		// Update the last shown date when the splash screen is shown
		setSplashScreenLastShownDate(new Date().toISOString());

		// Move the splash screen to the next season after one second
		const timeout = setTimeout(
			() => {
				if (
					!currentlyShownSeasons ||
					currentlyShownSeasons.length === seasons.length
				)
					return;

				const index = seasonsSortedCurrentOneLast.indexOf(
					currentlyShownSeasons[currentlyShownSeasons.length - 1],
				);
				setCurrentlyShownSeasons([
					...currentlyShownSeasons,
					seasonsSortedCurrentOneLast[index + 1],
				]);
			},
			currentlyShownSeasons.length === 0 ? 1000 : 300,
		);

		return () => clearTimeout(timeout);
	}, [
		currentlyShownSeasons,
		seasonsSortedCurrentOneLast,
		setSplashScreenLastShownDate,
	]);

	return (
		<div
			onClick={() => {
				if (shouldMountSplashScreen) {
					setShouldMountSplashScreen(false);
					setCurrentlyShownSeasons(seasonsSortedCurrentOneLast);
				}
			}}>
			{shouldMountSplashScreen && (
				<SplashScreenAnimation
					seasonsSortedCurrentOneLast={seasonsSortedCurrentOneLast}
					currentlyShownSeasons={currentlyShownSeasons}
					onFinished={() => setShouldMountSplashScreen(false)}
				/>
			)}
			{currentlyShownSeasons.length >= seasons.length - 2 && (
				<div
					className={`transition-opacity ${
						shouldMountSplashScreen
							? 'delay-[1.5s] duration-[1.5s]'
							: ''
					} ${
						currentlyShownSeasons.length < seasons.length
							? 'pointer-events-none opacity-0'
							: 'opacity-100'
					}`}>
					{children}
				</div>
			)}
		</div>
	);
};

const SplashScreenAnimation = ({
	seasonsSortedCurrentOneLast,
	currentlyShownSeasons,
	onFinished,
}: {
	seasonsSortedCurrentOneLast: Season[];
	currentlyShownSeasons: Season[];
	onFinished: () => void;
}) => {
	const isNative = useIsNative();

	const isUpdating = useMemo(() => {
		// Check if isUpdating is present in search params or in localStorage
		const isUpdating =
			localStorage.getItem('soortsafari-isUpdating') ||
			location.search.includes('isUpdating');

		// Delete the localStorage item
		if (isUpdating) localStorage.removeItem('soortsafari-isUpdating');

		return !!isUpdating;
	}, []);

	// Finish the splash screen 2.5 seconds after the last season has been shown
	useEffect(() => {
		if (currentlyShownSeasons.length === seasons.length) {
			const timeout = setTimeout(() => {
				onFinished();
			}, 2500);
			return () => clearTimeout(timeout);
		}
	}, [currentlyShownSeasons, onFinished]);

	return (
		<div
			className="absolute inset-0 size-full overflow-hidden transition-all delay-[2s] duration-1000"
			style={{
				opacity:
					currentlyShownSeasons.length === seasons.length ? 0 : 1,
			}}>
			<div
				className="fixed inset-0 z-50 grid size-full grid-cols-2 grid-rows-2 transition-all delay-1000 duration-1000 ease-in-out will-change-transform"
				style={{
					width: '100lvw',
					height: '100lvh',
					backgroundColor: isNative ? 'transparent' : 'black',
					transformOrigin: '0% 0%',
					// Make it so it zooms in on the last season and makes that full screen
					transform:
						currentlyShownSeasons.length < seasons.length - 1
							? ''
							: 'scale(2) translate(-50%, -50%)',
					filter:
						currentlyShownSeasons.length === seasons.length
							? 'brightness(50%)'
							: 'brightness(100%)',
				}}>
				{seasonsSortedCurrentOneLast.map((season, index) => (
					<div
						key={season}
						className="relative size-full transition-all delay-300 duration-[2s]"
						style={{
							opacity:
								currentlyShownSeasons.length ===
									seasons.length &&
								index !== seasons.length - 1
									? 0.4
									: 1,
							transform: `scale(${
								currentlyShownSeasons.length ===
									seasons.length &&
								index !== seasons.length - 1
									? 0
									: 1
							})`,
						}}>
						{index === seasons.length - 1 && (
							<>
								<div
									className={`will-change-opacity absolute size-full transition-opacity delay-500 duration-1000 ease-in ${
										currentlyShownSeasons.length <
										seasons.length
											? 'opacity-0'
											: 'opacity-100'
									}`}
									style={{
										backgroundColor: 'var(--color-primary)',
										backgroundImage: `url(/assets/backgrounds/${season}.svg)`,
									}}
								/>
								<img
									alt=""
									src={`/assets/illustrations/bottom_end_${season}.svg`}
									className={`absolute bottom-0 end-0 z-50 max-w-xs origin-bottom-right translate-x-1/4 scale-50 transition-transform delay-300 duration-700 ease-out will-change-transform ${
										currentlyShownSeasons.length >=
										seasons.length - 1
											? 'translate-y-0'
											: 'translate-y-full'
									}`}
								/>
							</>
						)}
						<div
							className="absolute size-full transition-all duration-300 will-change-transform"
							style={{
								backgroundColor: 'var(--color-primary)',
								backgroundImage: `url(/assets/backgrounds/${season}.svg)`,
								backgroundSize:
									index !== seasons.length - 1
										? '200%, auto, cover'
										: 'cover',
								backgroundPositionY:
									index !== seasons.length - 1
										? 'top'
										: 'center',

								WebkitMaskImage: `url("/assets/masks/blob_square_${
									index + 1
								}.svg")`,
								maskImage: `url("/assets/masks/blob_square_${
									index + 1
								}.svg")`,
								maskPosition: 'center',
								maskRepeat: 'no-repeat',
								opacity: currentlyShownSeasons.includes(season)
									? 1
									: 0,
								transform: `rotate(${
									(seasons.length - 1 - index) * 2
								}deg) scale(${
									currentlyShownSeasons.includes(season)
										? index === seasons.length - 1
											? 1
											: 1.25
										: 0
								})`,
							}}
						/>
					</div>
				))}
			</div>
			{isUpdating && (
				<div className="fixed inset-x-0 bottom-16 z-50 flex items-center justify-center">
					<p
						role="status"
						className="relative z-50 px-4 py-2 font-brand text-2xl text-white/90">
						<LabelBackground className="text-black" />{' '}
						<span className="relative">Bijwerken...</span>
					</p>
				</div>
			)}
			{currentlyShownSeasons.length >= seasons.length - 1 && (
				<div
					className={`fixed inset-0 z-50 flex size-full animate-fade-in items-center justify-center p-24 transition-all duration-1000 ${
						currentlyShownSeasons.length === seasons.length
							? 'scale-150'
							: 'scale-0'
					}`}>
					<WordMarkLogo withShadow isAnimated={false} />
				</div>
			)}
		</div>
	);
};

export default SplashScreen;
